import React, { useState, useMemo } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { Reveal } from '../components/ui-kit/Reveal';
import { faqLd } from '../lib/schema';
import CTA from '../components/CTA';
import MidCTA from '../components/MidCTA';

const Ukk = () => {
  const { t } = useLanguage();

  // FAQ JSON-LD derived from the SAME visible source (t.faq.items) so schema
  // never diverges from what the user actually sees.
  const jsonLd = useMemo(
    () => [faqLd(t.faq.items.map((it) => ({ q: it.question, a: it.answer })))],
    [t.faq.items]
  );

  usePageMeta({
    title: 'Agencive – Usein kysytyt kysymykset',
    description:
      'Vastauksia yleisiin kysymyksiin brändiagenteista, asennuksesta olemassa oleville sivustoille ja käyttötapauksista.',
    path: '/ukk',
    jsonLd,
  });

  const [openIdx, setOpenIdx] = useState(0);

  const renderItem = (item, i) => {
    const isOpen = openIdx === i;
    return (
      <li key={item.question}>
        <button
          type="button"
          onClick={() => setOpenIdx(isOpen ? -1 : i)}
          aria-expanded={isOpen}
          data-testid={`faq-page-item-${i}`}
          className="w-full flex items-center justify-between gap-6 py-6 text-left group"
        >
          <span className="text-[17px] md:text-[18px] font-medium text-ink leading-snug pr-4">{item.question}</span>
          <span
            className={
              'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-pill border border-line transition-colors ' +
              (isOpen ? 'bg-ink text-white border-ink' : 'text-ink-muted group-hover:border-ink group-hover:text-ink')
            }
          >
            {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </span>
        </button>
        <div
          className="overflow-hidden transition-[max-height,opacity] duration-300 ease-out-quart"
          style={{ maxHeight: isOpen ? 360 : 0, opacity: isOpen ? 1 : 0 }}
          aria-hidden={!isOpen}
        >
          <p className="pb-6 pr-12 text-body text-ink-muted max-w-3xl">{item.answer}</p>
        </div>
      </li>
    );
  };

  const firstBatch = t.faq.items.slice(0, 3);
  const restBatch = t.faq.items.slice(3);

  return (
    <main className="bg-bg">
      <section className="pt-28 md:pt-32 pb-12 md:pb-16">
        <div className="container-page max-w-3xl">
          <Reveal>
            <span className="eyebrow text-ink-muted">{t.faq.eyebrow}</span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 text-h1 text-ink">{t.faq.title}</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-body-lg text-ink-muted">{t.faq.subtitle}</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-4">
        <div className="container-page">
          <ul className="max-w-3xl divide-y divide-line border-y border-line" data-testid="faq-list-top">
            {firstBatch.map((item, i) => renderItem(item, i))}
          </ul>
        </div>
      </section>

      {restBatch.length > 0 && (
        <>
          <MidCTA
            title="Onko teillä vielä kysymyksiä?"
            subtitle="Käydään asia läpi 20 minuutin maksuttomassa kartoituksessa. Saat arvion sopivasta käyttötapauksesta, toteutuksesta ja seuraavista askelista."
          />

          <section className="pb-20 md:pb-28">
            <div className="container-page">
              <ul className="max-w-3xl divide-y divide-line border-y border-line" data-testid="faq-list-rest">
                {restBatch.map((item, i) => renderItem(item, i + firstBatch.length))}
              </ul>
            </div>
          </section>
        </>
      )}

      <CTA />
    </main>
  );
};

export default Ukk;
